
import { postRequest } from '../utils/PostRequest';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { RootState } from "../../root-redux/RootState";
import { v4 as uuidv4 } from 'uuid';
import { Logger } from '../../utils/Logger';
import { CampaignTypes, CampaignTypesEnum, SPA_ADMIN_MAIL, SPA_DRI_ROLE, SPA_REVIEWER_ROLE, SPA_SubscriptionKey } from '../../feature-components/spa-admin/SPAConstants';

const getRedirectHost = (host: string, id: string, env: string) => {
    return host + (host.endsWith('/') ? "redirect/" : "/redirect/") + env  + "/Campaign/" + id;
}

const getSegmentQuery = (campaignType: string) => {
    return (campaignType).startsWith("Enterprise") ? ["Enterprise"] : 
           (campaignType).startsWith("DSE") ? ["DSE"] :
           (campaignType).startsWith("MultiSegment") ? ['SMC', 'DES', 'DSE', 'Enterprise'] : ['SMC'];
}

const getSolutionPlayQuery = (solutionPlay: string) => {
    return solutionPlay.substring(solutionPlay.length - 30, solutionPlay.length);
}

// Disabled due to noise
export const sendSPANotificationKickOff = 
  createAsyncThunk<any, {workflowId: string}, { state: RootState }>
  ('campaigns/notification/SPA', 
  async ({workflowId}, { getState }) => {
    const host = process.env.REACT_APP_REDIRECT_URL || "";
    const selectedEnv = getState().env.selectedEnvItem;
    const campaignById = getState().campaignById;
    const userDetails = getState().userDetails;
    const TEMPLATE_ID = "70d5cffc-ca5f-4230-8d3a-7a44d66a8d5a";
    const isTestMail = selectedEnv === 'dev' || getState().userGroups.list.includes(SPA_ADMIN_MAIL);

    const segment = (campaignById.summary["CampaignType"] || "").startsWith("Enterprise") ? "Enterprise" : 'SMC';
    const correlationId = uuidv4();

    const templateObjectId = segment === 'SMC' ? "673941eb-0826-4122-b9e0-cdd3c96c5dbe" : "37a5b17b-d022-4a1b-a856-07a7ba161c8e";
    const templateUserEmail = segment === 'SMC' ? "ASPA-SMCC_DSE-Govern@microsoft.com" : "ASPA-Ent-Governance@microsoft.com";
    const templateUserName = segment === 'SMC' ? "ASPA-SMCC/DSE Governance" : "ASPA-Ent-Governance";

    const spaNotiDetails = {
        "correlationId": correlationId,
        "items": [
            {
                "dataValues": {
                    "SPAName": campaignById?.summary["CampaignName"]!,
                    "SPADRIName": userDetails.name,
                    "Alias": userDetails.username.split('@')[0],
                    "SolutionArea": campaignById?.summary["SolutionArea"]!,
                    "SalesMotion": campaignById?.summary["SalesMotion"]!,
                    "SolutionPlay": campaignById?.summary["SolutionPlay"]!,
                    "Description": campaignById?.summary["Description"]!,
                    "DirectSPALink": getRedirectHost(host, workflowId, selectedEnv),
                    "unsubscribeLink": "unsubscribeLink",
                    "manageNotificationLink": "manageNotificationLink",
                    "id": TEMPLATE_ID,
                    "To": `[{"ObjectId":"${templateObjectId}","EmailAddress":"${templateUserEmail}"}]`,
                    "UserName": templateUserName
                },
                "priority": 10,
                "referenceid": correlationId,
                "state": "New"
            }
        ],
        "notificationConfigurationId": TEMPLATE_ID,
        "source": host
    }
    var environment = selectedEnv;
    const response = await postRequest<any, any>(`${selectedEnv}/campaigns/notification/SPA?test=${isTestMail}`, spaNotiDetails, userDetails.localAccountId, false, correlationId, SPA_SubscriptionKey);
    const data: any = response.data;
    if ((data["Code"] || "").startsWith('ERR')) {
        Logger.Instance.trackException(data["Code"], {
            env: environment,
            "API": `${environment}/campaigns/notification/SPA`,            
            "CampaignId": campaignById.campaign?.config.workflowId!,
            "CampaignName": campaignById?.summary["CampaignName"]!,
            "Success": "false",
            "correlationId": correlationId,
            "Method" : "sendSPANotificationKickOff"
        });

    } else {
        Logger.Instance.trackEvent(data["Code"], {
            env: environment,
            "API": `${environment}/campaigns/notification/SPA`,            
            "CampaignId": campaignById.campaign?.config.workflowId!,
            "CampaignName": campaignById?.summary["CampaignName"]!,
            "Success": "true",
            "correlationId": correlationId,
            "Method" : "sendSPANotificationKickOff"
        });

    }
    return response.data;
});

// Gets triggered when SPA DRI clicks on Share button in Datacart page
export const sendAreaReviewerNotification = createAsyncThunk<any, boolean, { state: RootState }>('campaigns/notification/AreaReviewer', async (sendFlag, { getState, rejectWithValue }) => {
    const selectedEnv = getState().env.selectedEnvItem;
    const campaignById = getState().campaignById;
    const userDetails = getState().userDetails;
    const workflowId = getState().campaignById.campaign?.config.workflowId || "";
    const host = process.env.REACT_APP_REDIRECT_URL || "";
    const TEMPLATE_ID = "3cfd3025-3f67-4c30-ac67-4ed825695002";
    const isTestMail = selectedEnv === 'dev' || getState().userGroups.list.includes(SPA_ADMIN_MAIL);

    const correlationId = uuidv4();

    const spaNotiDetails = {
        "correlationId": correlationId,
        "items": [
            {
                "dataValues": {
                    "SPAName": campaignById?.summary["CampaignName"]!,
                    "SPADRIName": userDetails.name,
                    "Alias": userDetails.username.split('@')[0],
                    "SolutionArea": campaignById?.summary["SolutionArea"]!,
                    "SalesMotion": campaignById?.summary["SalesMotion"]!,
                    "SolutionPlay": campaignById?.summary["SolutionPlay"]!,
                    "Description": campaignById?.summary["Description"]!,
                    "MSXEnvlink": getRedirectHost(host, workflowId, selectedEnv),
                    "NumberoFDaystoReview": 14,
                    "unsubscribeLink": "unsubscribeLink",
                    "manageNotificationLink": "manageNotificationLink",
                    "id": TEMPLATE_ID,
                    "To": `[To be Filled by API]`,
                    "UserName": 'ASPA Area reviwers'
                },
                "priority": 10,
                "referenceid": correlationId,
                "state": "New"
            }
        ],
        "notificationConfigurationId": TEMPLATE_ID,
        "source": host
    }
    var environment = selectedEnv;
    const requestData = {
        "notificationRequest": spaNotiDetails,
        "securityRole": SPA_REVIEWER_ROLE,
        "userSettingFilters": {
            "ASPASegment": getSegmentQuery(campaignById.summary["CampaignType"] || ""),
            "ASPASolutionPlay": [getSolutionPlayQuery(campaignById?.summary["SolutionPlay"]!)]
        },
        "SPAOwner": {
            "OwnerName": userDetails.name,
            "OwnerDomain": userDetails.username,
            "OwnerObjectId": userDetails.localAccountId
        }
    }

    const response = await postRequest<any, any>(`${selectedEnv}/taxonomy/notify/users?test=${isTestMail}&send=${sendFlag}`, requestData, userDetails.localAccountId, false, correlationId, SPA_SubscriptionKey);
    const data: any = response.data;
        if ((data["Code"] || "").startsWith('ERR')) {
            Logger.Instance.trackException(data["Code"], {
                env: environment,
                "API": "campaigns/notification/AreaReviewer",
                "Method": "sendAreaReviewerNotification",
                "CampaignId": campaignById.campaign?.config.workflowId!,
                "CampaignName": campaignById?.summary["CampaignName"]!,
                "Success": "false",
                "correlationId": correlationId,
            });
        } else {
            Logger.Instance.trackEvent(data["Code"], {
                env: environment,
                "API": "campaigns/notification/AreaReviewer",
                "Method": "sendAreaReviewerNotification",
                "CampaignId": campaignById.campaign?.config.workflowId!,
                "CampaignName": campaignById?.summary["CampaignName"]!,
                "Success": "true",
                "correlationId": correlationId,
            });
        }

    return response.data;
});

// Only gets triggered after clicking on the save from publish page.
// Audience: The Governance team will receive a mail
// This is non-repeating mail, only triggeres when a new campaign gets created
export const sendVTeamReviewNotification = createAsyncThunk<any, void, { state: RootState }>('campaigns/notification/VTeamReviewer', async (_, { getState, rejectWithValue }) => {
    const selectedEnv = getState().env.selectedEnvItem;
    const campaignById = getState().campaignById;
    const userDetails = getState().userDetails;
    const workflowId = getState().campaignById.campaign?.config.workflowId || "";
    const host = process.env.REACT_APP_REDIRECT_URL || "";
    const TEMPLATE_ID = "d8a8ead0-5726-40db-ad86-4cdf5f6dad77";
    const isTestMail = selectedEnv === 'dev' || getState().userGroups.list.includes(SPA_ADMIN_MAIL);
    const campaignType = CampaignTypes.find(x => x[0] === (campaignById?.summary["CampaignType"] || CampaignTypesEnum.SMC.toString()));

    const correlationId = uuidv4();

    const formatCurrency = (value: string|undefined): string|null => {
        if (value == undefined) return null;
        const num = Number(value);
        if (isNaN(num)) return value;
        return num.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
    };

    const getDurationInDays = (startOnDate: string | undefined, expiryDate: string | undefined): string | null => {
        if (startOnDate == undefined || expiryDate == undefined)
                return null;
        const parseDate = (dateStr: string) => {
            // MM/DD/YYYY
            const [month, day, year] = dateStr.split('/').map(Number);
            return new Date(year, month - 1, day);
        };
        try {
            const start = parseDate(startOnDate);
            const end = parseDate(expiryDate);
            const diffMs = end.getTime() - start.getTime();
            return Math.ceil(diffMs / (1000 * 60 * 60 * 24)).toString() + " days";
        } catch {
            return null;
        }
    };

    const triggerVTeamReviewNotification = async (meoMailObjectId: string, meoUserEmail: string, meoUserName: string) => {
        const spaNotiDetails = {
            "correlationId": correlationId,
            "items": [
                {
                    "dataValues": {
                        "SPAName": campaignById?.summary["CampaignName"]!,
                        "SPADRIName": userDetails.name,
                        "Alias": userDetails.username.split('@')[0],
                        "SolutionArea": campaignById?.summary["SolutionArea"]!,
                        "SalesMotion": campaignById?.summary["SalesMotion"]!,
                        "SolutionPlay": campaignById?.summary["SolutionPlay"]!,
                        "Description": campaignById?.summary["Description"]!,
                        "MSXEnvlink": getRedirectHost(host, workflowId, selectedEnv),
                        "unsubscribeLink": "unsubscribeLink",
                        "manageNotificationLink": "manageNotificationLink",
                        "TargetSegment" : campaignType ? campaignType[1] : null,
                        "AccountTargetLogic": campaignById?.summary["Description"] || null,
                        "Duration": getDurationInDays(campaignById?.summary["StartOn"], campaignById?.summary["ExpiryDate"]),
                        "TargetPipeline": formatCurrency(campaignById?.summary["TargetedPipeline"]),
                        "BypassJustification": campaignById?.summary["ByPassCoolDownLogicJustification"],
                        "AttachedOffer": campaignById?.summary["ApprovedOffer"] || null,
                        "id": TEMPLATE_ID,
                        "To": `[{"ObjectId":"${meoMailObjectId}","EmailAddress":"${meoUserEmail}"}]`,
                        "UserName": meoUserName
                    },
                    "priority": 10,
                    "referenceid": correlationId,
                    "state": "New"
                }
            ],
            "notificationConfigurationId": TEMPLATE_ID,
            "source": host
        }
        var environment = selectedEnv;

        const response = await postRequest<any, any>(`${selectedEnv}/campaigns/notification/SPA?test=${isTestMail}`, spaNotiDetails, userDetails.localAccountId, false, correlationId, SPA_SubscriptionKey);
        const data: any = response.data;
            if ((data["Code"] || "").startsWith('ERR')) {
                Logger.Instance.trackException(data["Code"], {
                    env: environment,
                    "API": "campaigns/notification/SPA",                
                    "CampaignId": campaignById.campaign?.config.workflowId!,
                    "CampaignName": campaignById?.summary["CampaignName"]!,
                    "Success": "false",
                    "Method" : "sendVTeamReviewNotification"
                });
            } else {
                Logger.Instance.trackEvent(data["Code"], {
                    env: environment,
                    "API": "campaigns/notification/SPA",                
                    "CampaignId": campaignById.campaign?.config.workflowId!,
                    "CampaignName": campaignById?.summary["CampaignName"]!,
                    "Success": "true",
                    "Method" : "sendVTeamReviewNotification"
                });
            }
        return response.data;
    }

    const segment = (((campaignById.summary["CampaignType"] || "").startsWith("Enterprise")) ||
    ((campaignById.summary["CampaignType"] || "").startsWith("DSE"))) ? "Enterprise" : (campaignById.summary["CampaignType"] || "").startsWith("SMC")
    ? 'SMC' :'Multi';

    const SMC_ObjectId = "673941eb-0826-4122-b9e0-cdd3c96c5dbe";
    const Enterprise_ObjectId = "37a5b17b-d022-4a1b-a856-07a7ba161c8e";
    const SMC_UserEmail = "ASPA-SMCC_DSE-Govern@microsoft.com"
    const Enterprise_UserEmail = "ASPA-Ent-Governance@microsoft.com";
    const SMC_UserName = "ASPA-SMCC/DSE Governance";
    const Enterprise_UserName = "ASPA-Ent-Governance";

  
    if (segment === "SMC")
        return await triggerVTeamReviewNotification(SMC_ObjectId, SMC_UserEmail, SMC_UserName);
    else if (segment == "Enterprise")
        return await triggerVTeamReviewNotification(Enterprise_ObjectId, Enterprise_UserEmail, Enterprise_UserName);
    
    else {
        triggerVTeamReviewNotification(SMC_ObjectId, SMC_UserEmail, SMC_UserName);
        return await triggerVTeamReviewNotification(Enterprise_ObjectId, Enterprise_UserEmail, Enterprise_UserName);
    }
});

// This also gets triggered only when a new Campaign gets created
// Audiences are the SPA DRI's who are part of same Solution play (onboarded part of msx user settings)
export const sendNotificationAboutNewLaunch = createAsyncThunk<any, {workflowId: string}, { state: RootState }>('campaigns/notification/VTeamReviewer', async ( { workflowId }, { getState, rejectWithValue }) => {
    const selectedEnv = getState().env.selectedEnvItem;
    const campaignById = getState().campaignById;
    const userDetails = getState().userDetails;
    const host = process.env.REACT_APP_REDIRECT_URL || "";
    const TEMPLATE_ID = "8540725f-0537-4221-ab66-587a0a44399e";
    const isTestMail = selectedEnv === 'dev' || getState().userGroups.list.includes(SPA_ADMIN_MAIL);

    const correlationId = uuidv4();

    const spaNotiDetails = {
        "correlationId": correlationId,
        "items": [
            {
                "dataValues": {
                    "SPAName": campaignById?.summary["CampaignName"]!,
                    "SolutionArea": campaignById?.summary["SolutionArea"]!,
                    "SalesMotion": campaignById?.summary["SalesMotion"]!,
                    "SolutionPlay": campaignById?.summary["SolutionPlay"]!,
                    "Description": campaignById?.summary["Description"]!,
                    "MSXEnvlink": getRedirectHost(host, workflowId, selectedEnv),
                    "unsubscribeLink": "unsubscribeLink",
                    "manageNotificationLink": "manageNotificationLink",
                    "id": TEMPLATE_ID,
                    "To": `[To be Filled]`,
                    "UserName": "ASPA V-Team"
                },
                "priority": 10,
                "referenceid": correlationId,
                "state": "New"
            }
        ],
        "notificationConfigurationId": TEMPLATE_ID,
        "source": host
    }
    var environment = selectedEnv;
    const requestData = {
        "notificationRequest": spaNotiDetails,
        "securityRole": SPA_DRI_ROLE,
        "userSettingFilters": {
            "ASPASegment": getSegmentQuery(campaignById.summary["CampaignType"] || ""),
            "ASPASolutionPlay": [getSolutionPlayQuery(campaignById?.summary["SolutionPlay"]!)]
        },
        "SPAOwner": {
            "OwnerName": userDetails.name,
            "OwnerDomain": userDetails.username,
            "OwnerObjectId": userDetails.localAccountId
        }
    }

    const response = await postRequest<any, any>(`${selectedEnv}/taxonomy/notify/users?test=${isTestMail}&send=true`, requestData, userDetails.localAccountId, false, correlationId, SPA_SubscriptionKey);
    const data: any = response.data;
        if ((data["Code"] || "").startsWith('ERR')) {
            Logger.Instance.trackException(data["Code"], {
                env: environment,
                "API": "campaigns/notification/SPA",                
                "CampaignId": campaignById.campaign?.config.workflowId!,
                "CampaignName": campaignById?.summary["CampaignName"]!,
                "Success": "false",
                "correlationId": correlationId,
                "Method" : "sendNotificationAboutNewLaunch"
            });
        } else {
            Logger.Instance.trackEvent(data["Code"], {
                env: environment,
                "API": "campaigns/notification/SPA",                
                "CampaignId": campaignById.campaign?.config.workflowId!,
                "CampaignName": campaignById?.summary["CampaignName"]!,
                "Success": "true",
                "correlationId": correlationId,
                "Method" : "sendNotificationAboutNewLaunch"
            });
        }

    return response.data;
});

// Gets triggered for every next click, i.e. update of the campaign details
export const sendNotificationAboutUpdate = createAsyncThunk<any, {workflowId: string}, { state: RootState }>('campaigns/notification/VTeamReviewer', async ({workflowId}, { getState, rejectWithValue }) => {
    const selectedEnv = getState().env.selectedEnvItem;
    const campaignById = getState().campaignById;
    const userDetails = getState().userDetails;
    const host = process.env.REACT_APP_REDIRECT_URL || "";
    const TEMPLATE_ID = "11ff0109-a651-4673-8eb3-4184d5f0356c";
    const isTestMail = selectedEnv === 'dev' || getState().userGroups.list.includes(SPA_ADMIN_MAIL);

    const correlationId = uuidv4();

    const spaNotiDetails = {
        "correlationId": correlationId,
        "items": [
            {
                "dataValues": {
                    "SPAName": campaignById?.summary["CampaignName"]!,
                    "SPAUpdates": "Summary Changes",
                    "SolutionArea": campaignById?.summary["SolutionArea"]!,
                    "SalesMotion": campaignById?.summary["SalesMotion"]!,
                    "SolutionPlay": campaignById?.summary["SolutionPlay"]!,
                    "Description": campaignById?.summary["Description"]!,
                    "MSXEnvlink": getRedirectHost(host, workflowId, selectedEnv),
                    "unsubscribeLink": "unsubscribeLink",
                    "manageNotificationLink": "manageNotificationLink",
                    "id": TEMPLATE_ID,
                    "To": `[To be Filled]`,
                    "UserName": "ASPA V-Team"
                },
                "priority": 10,
                "referenceid": correlationId,
                "state": "New"
            }
        ],
        "notificationConfigurationId": TEMPLATE_ID,
        "source": host
    }
    var environment = selectedEnv;
    const requestData = {
        "notificationRequest": spaNotiDetails,
        "securityRole": SPA_DRI_ROLE,
        "userSettingFilters": {
            "ASPASegment": getSegmentQuery(campaignById.summary["CampaignType"] || ""),
            "ASPASolutionPlay": [getSolutionPlayQuery(campaignById?.summary["SolutionPlay"]!)]
        },
        "SPAOwner": {
            "OwnerName": userDetails.name,
            "OwnerDomain": userDetails.username,
            "OwnerObjectId": userDetails.localAccountId
        }
    }

    const response = await postRequest<any, any>(`${selectedEnv}/taxonomy/notify/users?test=${isTestMail}&send=true`, requestData, userDetails.localAccountId, false, correlationId, SPA_SubscriptionKey);
    const data: any = response.data;
        if ((data["Code"] || "").startsWith('ERR')) {
            Logger.Instance.trackException(data["Code"], {
                env: environment,
                "API": "campaigns/notification/SPA",                
                "CampaignId": campaignById.campaign?.config.workflowId!,
                "CampaignName": campaignById?.summary["CampaignName"]!,
                "Success": "false",
                "correlationId": correlationId,
                "Method" : "sendNotificationAboutUpdate"
            });
        } else {
            Logger.Instance.trackEvent(data["Code"], {
                env: environment,
                "API": "campaigns/notification/SPA",                
                "CampaignId": campaignById.campaign?.config.workflowId!,
                "CampaignName": campaignById?.summary["CampaignName"]!,
                "Success": "true",
                "correlationId": correlationId,
                "Method" : "sendNotificationAboutUpdate"
            });
        }
        
    return response.data;
});
